import { round, sumBy } from 'lodash';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import InfoIcon from 'images/info-circle.svg';
import Loader from 'layout/atoms/Loader/Loader';
import TableGrid, { Body, Cell, Head, Row } from 'layout/atoms/TableGrid';
import EmptyState from 'layout/molecules/EmptyState/EmptyState';
import Grid from 'layout/molecules/Grid/Grid';
import { openModal } from 'store/layoutSlice';
import { fetchSpecificationAverageCostDetails, fetchSpecificationCosts, fetchSpecificationVariantCostDetails, resetSpecificationCosts, selectSpecificationCostsColors, selectSpecificationCostsRefetchFlags, selectSpecificationCostsSizes, selectSpecificationCostsStatus, selectSpecificationCostsVariants, } from 'store/specificationCostsSlice';
function EstimatedCosts() {
    var _a;
    const { t } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const specificationDetails = useSelector((state) => state.specificationPreview.item);
    const status = useSelector(selectSpecificationCostsStatus);
    const refetchFlag = useSelector(selectSpecificationCostsRefetchFlags);
    const sizes = useSelector(selectSpecificationCostsSizes);
    const colors = useSelector(selectSpecificationCostsColors);
    const variants = useSelector(selectSpecificationCostsVariants);
    const availableSizes = new Set(sizes.map((size) => size.id));
    useEffect(() => {
        if (status.fetch === 'idle' || refetchFlag.fetch) {
            dispatch(fetchSpecificationCosts({ specificationId: specificationDetails.id }));
        }
    }, [dispatch, status, refetchFlag, specificationDetails.id]);
    useEffect(() => {
        return () => {
            dispatch(resetSpecificationCosts());
        };
    }, [dispatch]);
    const sortVariants = (sizesRow, variantsRow) => {
        const idOrderMap = new Map();
        sizesRow.forEach((item, index) => {
            idOrderMap.set(item.id, index);
        });
        return variantsRow.sort((a, b) => {
            var _a, _b;
            const indexA = (_a = idOrderMap.get(a.size.id)) !== null && _a !== void 0 ? _a : Infinity;
            const indexB = (_b = idOrderMap.get(b.size.id)) !== null && _b !== void 0 ? _b : Infinity;
            return indexA - indexB;
        });
    };
    if (status.fetch === 'idle' || status.fetch === 'loading') {
        return (React.createElement(Grid, { gap: 24 },
            React.createElement(Loader, null)));
    }
    if ((_a = specificationDetails === null || specificationDetails === void 0 ? void 0 : specificationDetails.estimatedCostCalculationProblems) === null || _a === void 0 ? void 0 : _a.some((item) => item.blocksSpecificationConfirmation)) {
        return (React.createElement(Grid, { gap: 24 },
            React.createElement(EmptyState, { header: t('specification_incomplete.header'), subheader: React.createElement(React.Fragment, null,
                    t('specification_incomplete.prefix'),
                    React.createElement("button", { type: "button", onClick: () => dispatch(openModal({ name: 'specificationProblems', data: { preview: true } })) }, t('specification_incomplete.link')),
                    t('specification_incomplete.suffix')) })));
    }
    return (React.createElement("div", { style: {
            width: 'calc(100% + 64px)',
            margin: '-24px 0 0 -32px',
            padding: '24px 32px 24px 32px',
            overflowX: 'scroll',
        } },
        React.createElement(Grid, { columns: 1, gap: 24 },
            React.createElement(TableGrid, { columns: [
                    ['120px', '1fr'],
                    ...sizes
                        .filter((size) => size.isActive)
                        .map(() => ['80px', '1fr']),
                    ['100px', '1fr'],
                ], columnBorders: true, rowBorders: true },
                React.createElement(Head, null,
                    React.createElement(Row, { head: true },
                        React.createElement(Cell, null, t('common:version')),
                        sizes.map((size) => size.isActive && React.createElement(Cell, { key: size.id }, size.sizeName.name)),
                        React.createElement(Cell, { align: "right" }, t('common:weighted_average')))),
                React.createElement(Body, null, colors.map((color) => (React.createElement(Row, { key: color.id },
                    React.createElement(Cell, null, color.name),
                    sortVariants(sizes, variants.filter((variant) => variant.color.id === color.id)).map((variant) => availableSizes.has(variant.size.id) && (React.createElement(Cell, { key: variant.id },
                        React.createElement("span", { "aria-hidden": "true", role: "button", onClick: () => {
                                dispatch(fetchSpecificationVariantCostDetails(variant.id));
                                dispatch(openModal({ name: 'estimatedCostsDetails' }));
                            } },
                            React.createElement(InfoIcon, null)),
                        variant.quantity,
                        "x ",
                        round(variant.estimatedCost, 2)))),
                    React.createElement(Cell, { align: "right" }, round(sumBy(variants.filter((variant) => variant.color.id === color.id), (item) => parseFloat(item.quantity) * parseFloat(item.estimatedCost)) /
                        sumBy(variants.filter((variant) => variant.color.id === color.id), 'quantity'), 2) || 0))))),
                React.createElement(Row, null,
                    React.createElement(Cell, null, t('common:weighted_average')),
                    sizes.map((size) => size.isActive && (React.createElement(Cell, { key: size.id }, round(sumBy(variants.filter((variant) => variant.size.id === size.id), (item) => parseFloat(item.quantity) * parseFloat(item.estimatedCost)) /
                        sumBy(variants.filter((variant) => variant.size.id === size.id), 'quantity'), 2) || 0))),
                    React.createElement(Cell, { align: "right" },
                        React.createElement("span", { "aria-hidden": "true", role: "button", onClick: () => {
                                dispatch(fetchSpecificationAverageCostDetails(specificationDetails.id));
                                dispatch(openModal({ name: 'estimatedCostsAverage' }));
                            } },
                            React.createElement(InfoIcon, null)),
                        round(sumBy(variants, (item) => item.quantity * item.estimatedCost) /
                            sumBy(variants, 'quantity'), 2) || 0))))));
}
export default EstimatedCosts;
